import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../services/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SitemarkIcon from "../components/landing/SitemarkIcon";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";
import toast from "react-hot-toast";

const ResetPasswordContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState("");

  const verifyToken = useCallback(async () => {
    try {
      const response = await apiService.verifyResetToken(token!);

      if (response.success && response.data) {
        setTokenValid(true);
        setEmail(response.data.email);
      } else {
        toast.error("This reset link is invalid or has expired");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (error) {
      toast.error("This reset link is invalid or has expired");
      setTimeout(() => navigate("/login"), 2000);
    } finally {
      setVerifying(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate("/login");
      return;
    }

    verifyToken();
  }, [token, navigate, verifyToken]);

  const validatePassword = (password: string) => {
    const errors = [];

    if (password.length < 8) {
      errors.push("at least 8 characters");
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push("one lowercase letter");
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push("one uppercase letter");
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push("one number");
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push("one special character (@$!%*?&)");
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(`Password must contain ${passwordErrors.join(", ")}`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.resetPassword(token!, newPassword);

      if (response.success) {
        setResetSuccess(true);
        toast.success("Password reset successfully!");
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setError(response.message || "Failed to reset password");
      }
    } catch (error: any) {
      console.error("Reset password error:", error);
      setError(error.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <>
        <CssBaseline enableColorScheme />
        <ResetPasswordContainer
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Verifying reset link...
          </Typography>
        </ResetPasswordContainer>
      </>
    );
  }

  if (!tokenValid) {
    return (
      <>
        <CssBaseline enableColorScheme />
        <ResetPasswordContainer direction="column" justifyContent="center">
          <Stack sx={{ justifyContent: "center", height: "100dvh", p: 2 }}>
            <Card
              variant="outlined"
              sx={{ maxWidth: 450, mx: "auto", width: "100%" }}
            >
              <CardContent sx={{ textAlign: "center", p: 4 }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  Invalid Reset Link
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  This password reset link is invalid or has expired.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate("/forgot-password")}
                >
                  Request new reset link
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </ResetPasswordContainer>
      </>
    );
  }

  if (resetSuccess) {
    return (
      <>
        <CssBaseline enableColorScheme />
        <ResetPasswordContainer direction="column" justifyContent="center">
          <Stack sx={{ justifyContent: "center", height: "100dvh", p: 2 }}>
            <Card
              variant="outlined"
              sx={{ maxWidth: 450, mx: "auto", width: "100%" }}
            >
              <CardContent sx={{ textAlign: "center", p: 4 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "success.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: 40, color: "success.main" }}
                  />
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Password Reset Complete!
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Your password has been successfully reset. Redirecting to
                  login...
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate("/login")}
                >
                  Go to login
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </ResetPasswordContainer>
      </>
    );
  }

  return (
    <>
      <CssBaseline enableColorScheme />
      <ResetPasswordContainer direction="column" justifyContent="center">
        <Box
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
          }}
        >
          <ColorModeIconDropdown />
        </Box>
        <Stack sx={{ justifyContent: "center", height: "100dvh", p: 2 }}>
          <Card
            variant="outlined"
            sx={{ maxWidth: 450, mx: "auto", width: "100%" }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 3 }}>
                <SitemarkIcon />
              </Box>

              <Typography variant="h4" sx={{ mb: 1 }}>
                Reset your password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter a new password for{" "}
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {email}
                </Typography>
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <TextField
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      label="New Password"
                      placeholder="••••••••"
                      required
                      fullWidth
                      variant="outlined"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError("");
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowNewPassword((v) => !v)}
                                size="small"
                                sx={{ ml: -5 }}
                                aria-label={showNewPassword ? "Hide password" : "Show password"}
                              >
                                {showNewPassword ? (
                                  <EyeSlashIcon style={{ width: 20, height: 20 }} />
                                ) : (
                                  <EyeIcon style={{ width: 20, height: 20 }} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      label="Confirm New Password"
                      placeholder="••••••••"
                      required
                      fullWidth
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setError("");
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword((v) => !v)}
                                size="small"
                                sx={{ ml: -5 }}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                              >
                                {showConfirmPassword ? (
                                  <EyeSlashIcon style={{ width: 20, height: 20 }} />
                                ) : (
                                  <EyeIcon style={{ width: 20, height: 20 }} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </FormControl>

                  <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Password requirements:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      <li>At least 8 characters long</li>
                      <li>One uppercase letter</li>
                      <li>One lowercase letter</li>
                      <li>One number</li>
                      <li>One special character (@$!%*?&)</li>
                    </Box>
                  </Alert>

                  {error && <Alert severity="error">{error}</Alert>}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? "Resetting password..." : "Reset password"}
                  </Button>

                  <Typography
                    sx={{ textAlign: "center", fontSize: "0.875rem" }}
                  >
                    <Link
                      onClick={() => navigate("/login")}
                      sx={{ cursor: "pointer" }}
                    >
                      Back to login
                    </Link>
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </ResetPasswordContainer>
    </>
  );
};

export default ResetPassword;
